import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface OrderPdfItem { label: string; qty: number; }

interface SupplierOrderPdfData {
    orderId: string;
    supplierName: string;
    supplierPhone?: string;
    supplierEmail?: string;
    supplierAddress?: string;
    supplierReferentName?: string;
    supplierReferentPhone?: string;
    supplierReferentEmail?: string;
    contactName: string;
    contactPhone?: string;
    contactEmail?: string;
    templateName: string;
    rdvDate?: string;
    rdvTime?: string;
    items: OrderPdfItem[];
    issuerName?: string;
    logoUrl?: string | null;
    brandColor?: string;
}

const detectImageFormat = (url: string): 'PNG' | 'JPEG' | 'WEBP' => {
    if (!url) return 'PNG';
    const lower = url.toLowerCase();
    if (lower.includes('image/png') || lower.endsWith('.png')) return 'PNG';
    if (lower.includes('image/webp') || lower.endsWith('.webp')) return 'WEBP';
    if (lower.includes('image/jpg') || lower.includes('image/jpeg') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'JPEG';
    return 'PNG';
};

const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#6d28d9').replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
    const [r, g, b] = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

// Assombrit une couleur pour obtenir un second ton de dégradé (miroir du --brand-2 de l'aperçu HTML validé
// par Emeni, 2026-08-20) — jsPDF n'a pas de dégradé natif, on simule avec des bandes verticales.
const darken = (rgb: [number, number, number], amount: number): [number, number, number] =>
    rgb.map((c) => Math.max(0, Math.round(c * (1 - amount)))) as [number, number, number];

// Fiche interne (jamais envoyée à personne) récapitulant une commande fournisseur déjà confirmée. Mise en
// page validée par Emeni le 2026-08-20 (aperçu HTML) : bandeau dégradé + badge, fiches Fournisseur/Client
// côte à côte (Client mis en valeur), date & heure centrée en évidence, tableau des articles.
export const generateSupplierOrderPdf = async (data: SupplierOrderPdfData) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const primary = hexToRgb(data.brandColor || '#6d28d9');
        const primary2 = darken(primary, 0.28);
        const onPrimary = getContrastRgb(data.brandColor || '#6d28d9');
        const ink: [number, number, number] = [34, 25, 51];
        const inkSoft: [number, number, number] = [91, 84, 112];
        const inkFaint: [number, number, number] = [138, 131, 160];
        const tint: [number, number, number] = [244, 241, 252];
        const stripe: [number, number, number] = [250, 248, 253];
        const border: [number, number, number] = [228, 223, 242];

        // ── Bandeau d'en-tête (dégradé simulé + badge) ──────────────────────
        const headerH = 34;
        const bands = 60;
        for (let i = 0; i < bands; i++) {
            const t = i / (bands - 1);
            const r = Math.round(primary[0] + (primary2[0] - primary[0]) * t);
            const g = Math.round(primary[1] + (primary2[1] - primary[1]) * t);
            const b = Math.round(primary[2] + (primary2[2] - primary[2]) * t);
            doc.setFillColor(r, g, b);
            doc.rect((210 / bands) * i, 0, 210 / bands + 0.5, headerH, 'F');
        }

        let logoDrawW = 0;
        if (data.logoUrl) {
            const maxW = 24;
            const maxH = 15;
            let drawW = maxW, drawH = maxH;
            try {
                const props = doc.getImageProperties(data.logoUrl);
                const ratio = props.width / props.height;
                if (ratio > maxW / maxH) { drawW = maxW; drawH = maxW / ratio; }
                else { drawH = maxH; drawW = maxH * ratio; }
            } catch (e) { /* repli sur le texte seul */ }
            try {
                doc.addImage(data.logoUrl, detectImageFormat(data.logoUrl), 15, 9, drawW, drawH);
                logoDrawW = drawW + 8;
            } catch (e) { console.warn('Could not render logo in PDF:', e); }
        }

        doc.setTextColor(onPrimary[0], onPrimary[1], onPrimary[2]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text((data.issuerName || 'Récapitulatif').toUpperCase(), 15 + logoDrawW, 21);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        const badgeLabel = 'RÉCAPITULATIF DE COMMANDE';
        const badgeW = doc.getTextWidth(badgeLabel) + 12;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(195 - badgeW, 12, badgeW, 9, 4.5, 4.5, 'F');
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.text(badgeLabel, 195 - badgeW / 2, 17.7, { align: 'center' });

        // ── Fiches Fournisseur / Client côte à côte ─────────────────────────
        let y = headerH + 15;
        const colW = 87;
        const leftX = 15, leftValX = 15 + 22;
        const rightX = 15 + colW + 6, rightValX = rightX + 22;

        // Fiche fournisseur complète (nom, contact référent, coordonnées, adresse) — demande explicite
        // d'Emeni (2026-08-20).
        const supplierRows: [string, string][] = [['Enseigne', data.supplierName || '']];
        if (data.supplierReferentName) supplierRows.push(['Référent', data.supplierReferentName]);
        if (data.supplierPhone) supplierRows.push(['Téléphone', data.supplierPhone]);
        if (data.supplierEmail) supplierRows.push(['Email', data.supplierEmail]);
        if (data.supplierReferentPhone) supplierRows.push(['Tél. référent', data.supplierReferentPhone]);
        if (data.supplierReferentEmail) supplierRows.push(['Email référent', data.supplierReferentEmail]);
        if (data.supplierAddress) supplierRows.push(['Adresse', data.supplierAddress]);

        // Fiche client volontairement minimale (nom, téléphone, email) — demande explicite d'Emeni,
        // pas de démarche ici.
        const clientRows: [string, string][] = [['Nom', data.contactName || '']];
        if (data.contactPhone) clientRows.push(['Téléphone', data.contactPhone]);
        if (data.contactEmail) clientRows.push(['Email', data.contactEmail]);

        const cardH = Math.max(supplierRows.length, clientRows.length) * 8.5 + 14;

        // Carte Fournisseur (neutre)
        doc.setLineWidth(0.2);
        doc.setFillColor(stripe[0], stripe[1], stripe[2]);
        doc.roundedRect(leftX, y, colW, cardH, 3, 3, 'F');
        doc.setDrawColor(border[0], border[1], border[2]);
        doc.roundedRect(leftX, y, colW, cardH, 3, 3, 'D');

        // Carte Client (mise en valeur — fond teinté couleur de marque + contour un peu plus foncé/opaque
        // que le violet de base, sans exagérer — demande explicite d'Emeni : "les contours plus foncés,
        // carte violet un peu plus opaque mais pas trop non plus").
        const clientBorder = darken(primary, 0.15);
        doc.setFillColor(tint[0], tint[1], tint[2]);
        doc.roundedRect(rightX, y, colW, cardH, 3, 3, 'F');
        doc.setLineWidth(0.8);
        doc.setDrawColor(clientBorder[0], clientBorder[1], clientBorder[2]);
        doc.roundedRect(rightX, y, colW, cardH, 3, 3, 'D');
        doc.setLineWidth(0.2);

        let cardTitleY = y + 9;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.text('FOURNISSEUR', leftX + 6, cardTitleY);
        doc.text('CLIENT', rightX + 6, cardTitleY);
        doc.setDrawColor(border[0], border[1], border[2]);
        doc.line(leftX + 6, cardTitleY + 2, leftX + colW - 6, cardTitleY + 2);
        doc.line(rightX + 6, cardTitleY + 2, rightX + colW - 6, cardTitleY + 2);

        let rowY = cardTitleY + 8.5;
        supplierRows.forEach(([label, value]) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
            doc.text(label, leftX + 6, rowY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(doc.splitTextToSize(value, colW - 14), leftValX, rowY);
            rowY += 8.5;
        });

        // Même style de texte que la carte Fournisseur (label discret, valeur normale) — demande explicite
        // d'Emeni, seuls le fond teinté et le contour distinguent encore la carte Client.
        rowY = cardTitleY + 8.5;
        clientRows.forEach(([label, value]) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
            doc.text(label, rightX + 6, rowY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(doc.splitTextToSize(value, colW - 14), rightValX, rowY);
            rowY += 8.5;
        });

        y += cardH + 10;

        // ── Date & heure — centrée, en évidence ─────────────────────────────
        if (data.rdvDate) {
            const dateLabel = format(new Date(`${data.rdvDate}T00:00:00`), 'EEEE d MMMM yyyy', { locale: fr }).toUpperCase();
            const fullLabel = data.rdvTime ? `${dateLabel} À ${data.rdvTime}` : dateLabel;
            const barH = 16;
            doc.setFillColor(tint[0], tint[1], tint[2]);
            doc.roundedRect(15, y, 180, barH, 3, 3, 'F');
            doc.setDrawColor(border[0], border[1], border[2]);
            doc.roundedRect(15, y, 180, barH, 3, 3, 'D');
            doc.setFillColor(primary[0], primary[1], primary[2]);
            doc.rect(15, y, 180, 1.4, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.setTextColor(primary2[0], primary2[1], primary2[2]);
            doc.text('DATE & HEURE', 105, y + 6.5, { align: 'center' });
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(fullLabel, 105, y + 12.5, { align: 'center' });
            y += barH + 10;
        }

        // ── Articles commandés ───────────────────────────────────────────────
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.text('DÉTAILS DES ARTICLES COMMANDÉS', 15, y);
        y += 7;

        doc.setFillColor(tint[0], tint[1], tint[2]);
        doc.rect(15, y - 4.5, 180, 8, 'F');
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.text('ARTICLE', 22, y);
        doc.text('QUANTITÉ', 175, y);
        y += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        data.items.forEach((it, idx) => {
            if (y > 270) { doc.addPage(); y = 20; }
            if (idx % 2 === 0) {
                doc.setFillColor(stripe[0], stripe[1], stripe[2]);
                doc.rect(15, y - 5, 180, 8, 'F');
            }
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(it.label, 22, y);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(primary[0], primary[1], primary[2]);
            doc.text(`x ${it.qty}`, 193, y, { align: 'right' });
            y += 8;
        });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
        doc.text(`Réf. ${data.orderId} — Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} — iAmani Connexion`, 105, 290, { align: 'center' });

        doc.save(`Commande_${data.supplierName.replace(/[^a-zA-Z0-9]/g, '_')}_${data.orderId}.pdf`);
    } catch (e) {
        console.error('Supplier Order PDF Generation Error:', e);
        alert('Erreur lors de la génération du document PDF.');
    }
};
